import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import * as actions from './actionCreators';
import SetupAuthenticator from './SetupAuthenticator';
import SetupPasskeyDialog from './SetupPasskeyDialog';
import PasskeyHelpDialog from './PasskeyHelpDialog';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import { useJspVariables, useAppContext, useAuth, useLoader, useDebugRender, usePageTitle } from '../hooks';
import { IceIcon, PageContainer, PageHeader, RadioGroup, TextInput, UserMenu, DialogTitleWithClose } from '../components';
import Button from '@mui/material/Button';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Unstable_Grid2';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';

const DeviceTableSkeleton = () => (
    <TableBody>
        {[0, 1].map(i => (
            <TableRow key={i}>
                <TableCell><Skeleton variant="text" width={140} /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={100} sx={{ mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton variant="circular" width={20} height={20} sx={{ mx: 'auto' }} /></TableCell>
            </TableRow>
        ))}
    </TableBody>
);

const EmptyDevicesRow = ({ message, colSpan = 3 }) => (
    <TableRow>
        <TableCell colSpan={colSpan} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 3 }}>
            {message}
        </TableCell>
    </TableRow>
);

const stripedTableBodySx = {
    '& tr:nth-of-type(even)': { backgroundColor: 'action.hover' },
};

const TfaFormSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="text" width={240} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rounded" height={40} />
    </Box>
);

const EMAIL_DELIVERY = 'EMAIL';
const WEBAUTHN_DELIVERY = 'WEBAUTHN';
const GOOGLE_AUTH_DELIVERY = 'ICE_MOBILE_APP';

const PageTfaRegister = () => {
    useDebugRender('PageTfaRegister');
    usePageTitle('Two-Factor Authentication');
    const navigate = useNavigate();
    const location = useLocation();
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);

    const { authenticatedUser, isAuthenticated, logout } = useAuth();
    const { showLoader, hideLoader } = useLoader();
    const [password] = useState('');

    // These two must remain in Redux — set by LoginTemplate/other pages before navigating here
    const pendingWebAuthn = useSelector(state => state.tfaReducer.tfaStatus.pendingWebAuthn);
    const registerAction = useSelector(state => state.tfaReducer.registerAction);

    // Local state — replaces Redux selectors
    const [userTfaDetails, setUserTfaDetails] = useState(null);
    const [webAuthnDevices, setWebAuthnDevices] = useState(null);
    const [authenticatorDevices, setAuthenticatorDevices] = useState(null);
    const [alert, setAlert] = useState(null);

    const [showAuthenticator, setShowAuthenticator] = useState(false);
    const [showHowTo, setShowHowTo] = useState(false);
    const [showPasskey, setShowPasskey] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const onPasskeyDeviceRegistered = useCallback(async () => {
        try {
            const resp = await actions.getWebAuthnRegistrationsAsync(
                authenticatedUser.authenticatedApp,
                authenticatedUser.authenticatedUser,
            );
            setWebAuthnDevices(resp.data || []);
        } catch (error) {
            console.error('Failed to refresh WebAuthn devices after registration', error);
         }
    }, [authenticatedUser]);
    const [showRegistrationDone, setShowRegistrationDone] = useState(false);
    const [passkeyHelpOpen, setPasskeyHelpOpen] = useState(false);

    const loadData = async () => {
        const skipTfaSessionCheck = location?.state?.skipTfaSessionCheck || false;
        const appKey = authenticatedUser.authenticatedApp;
        const userId = authenticatedUser.authenticatedUser;
        console.log('[PageTfaRegister] loadData', { skipTfaSessionCheck, appKey, userId, isAuthenticated });

        const [tfaResult, webAuthnResult, authDevicesResult] = await Promise.allSettled([
            actions.getTfaDetailsAuthenticatedAsync(skipTfaSessionCheck),
            actions.getWebAuthnRegistrationsAsync(appKey, userId),
            actions.loadAuthenticatorDevicesAsync(userId, appKey),
        ]);

        console.log('[PageTfaRegister] loadData results', { tfaResult: tfaResult.status, webAuthn: webAuthnResult.status, authDevices: authDevicesResult.status });
        if (tfaResult.status === 'rejected') console.log('[PageTfaRegister] tfaResult rejection reason', tfaResult.reason);

        if (tfaResult.status === 'fulfilled') {
            setUserTfaDetails(tfaResult.value.data);
        } else {
            console.log('[PageTfaRegister] getTfaDetailsAuthenticated failed, redirecting back to UpdateTfaLogin');
            navigate(constants.ROUTE_PAGE_UPDATE_TFA_LOGIN, { replace: true });
            return;
        }
        if (webAuthnResult.status === 'fulfilled') {
            setWebAuthnDevices(webAuthnResult.value.data || []);
        }
        if (authDevicesResult.status === 'fulfilled') {
            setAuthenticatorDevices(authDevicesResult.value.data || []);
        }
    };

    const refreshDeviceLists = async () => {
        const appKey = authenticatedUser.authenticatedApp;
        const userId = authenticatedUser.authenticatedUser;
        const [webAuthnResult, authDevicesResult] = await Promise.allSettled([
            actions.getWebAuthnRegistrationsAsync(appKey, userId),
            actions.loadAuthenticatorDevicesAsync(userId, appKey),
        ]);
        if (webAuthnResult.status === 'fulfilled') setWebAuthnDevices(webAuthnResult.value.data || []);
        if (authDevicesResult.status === 'fulfilled') setAuthenticatorDevices(authDevicesResult.value.data || []);
    };

    useEffect(() => {
        console.log('[PageTfaRegister] useEffect', { isAuthenticated, pendingWebAuthn, locationState: location?.state });
        if (pendingWebAuthn === 'Y') {
            return;
        }
        if (isAuthenticated) {
            loadData();
        } else {
            console.log('[PageTfaRegister] not authenticated, redirecting to UpdateTfaLogin');
            navigate(constants.ROUTE_PAGE_UPDATE_TFA_LOGIN, { replace: true });
        }

        if (registerAction === 'REGISTER_DEVICE') {
            setShowAuthenticator(true);
        } else if (registerAction === 'REGISTER_PASSKEY') {
            setShowPasskey(true);
        }
    }, []);

    const logoutUserToken = () => {
        logout(authenticatedUser.user);
    };

    const handleNavigate = (route) => {
        navigate(route);
    };

    const closeAllDialogs = () => {
        setShowAuthenticator(false);
        setShowHowTo(false);
        setShowPasskey(false);
        setDeleteTarget(null);
    };

    const onTfaDeliveryPreferenceOptionChange = (value, submit) => {
        if (value === WEBAUTHN_DELIVERY) {
            const mergedTfaDetails = { ...userTfaDetails, isActiveTfaWebAuthn: 'Y', isActiveTfaMobile: 'N', isActiveTfaEmail: 'N' };
            setUserTfaDetails(mergedTfaDetails);
            if (submit) {
                if (!mergedTfaDetails.tfaEmail) {
                    setAlert({ message: getLabelFromJspVariables('alert_email_required_tfa'), severity: 'error' });
                    return;
                }
                verifyNSaveTfaDetails(mergedTfaDetails);
            }
            return;
        }
        const updatedDeliveryPref = value === GOOGLE_AUTH_DELIVERY
            ? { isActiveTfaMobile: 'Y', isActiveTfaEmail: 'N' }
            : { isActiveTfaMobile: 'N', isActiveTfaEmail: 'Y' };
        const mergedTfaDetails = { ...userTfaDetails, ...updatedDeliveryPref, isActiveTfaWebAuthn: 'N' };
        setUserTfaDetails(mergedTfaDetails);
        if (submit) {
            if (!mergedTfaDetails.tfaEmail) {
                setAlert({ message: getLabelFromJspVariables('alert_email_required_tfa'), severity: 'error' });
                return;
            }
            verifyNSaveTfaDetails(mergedTfaDetails);
        }
    };

    const verifyNSaveTfaDetails = async (tfaOverride) => {
        const tfa = tfaOverride ? { ...tfaOverride } : { ...userTfaDetails };
        setAlert(null);
        showLoader('Saving TFA settings...');
        try {
            const resp = await actions.verifyNSaveTfaDetailsAsync(isAuthenticated, password, tfa);
            setUserTfaDetails(resp.data);
            setShowRegistrationDone(true);
        } catch (resp) {
            setAlert({ message: resp.message, severity: 'error' });
        } finally {
            hideLoader();
        }
    };

    const onFirstNameChange = (event) => {
        setUserTfaDetails(prev => ({ ...prev, firstName: event.target.value }));
    };

    const onLastNameChange = (event) => {
        setUserTfaDetails(prev => ({ ...prev, lastName: event.target.value }));
    };

    const onEmailChange = (event) => {
        const changedEmailValue = event.target.value;
        if (JspUtil.isValueAvailable(changedEmailValue)) {
            setUserTfaDetails(prev => ({ ...prev, tfaEmail: changedEmailValue.trim() }));
        }
    };

    const showSetupAuthenticatorDialog = () => {
        setAlert(null);
        setShowAuthenticator(true);
    };

    const showSetupPasskeyDialog = () => {
        setAlert(null);
        setShowPasskey(true);
    };

    const refreshTfaRegisterPage = () => {
        refreshDeviceLists();
        closeAllDialogs();
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const { row, isPassKey } = deleteTarget;
        const appKey = authenticatedUser.authenticatedApp;
        const userId = authenticatedUser.authenticatedUser;

        if (isPassKey) {
            if (userTfaDetails.isActiveTfaWebAuthn === 'Y' && webAuthnDevices.length === 1) {
                setAlert({ message: getLabelFromJspVariables('alert_change_delivery_before_delete'), severity: 'error', scope: 'DELETE_DIALOG' });
                return;
            }
            showLoader('Deleting passkey...');
            try {
                await actions.deleteWebAuthnRegistrationAsync(appKey, userId, row);
                const resp = await actions.getWebAuthnRegistrationsAsync(appKey, userId);
                setWebAuthnDevices(resp.data || []);
            } catch (resp) {
                setAlert({ message: resp.message, severity: 'error' });
            } finally {
                hideLoader();
            }
        } else {
            if (userTfaDetails.isActiveTfaMobile === 'Y' && authenticatorDevices.length === 1) {
                setAlert({ message: getLabelFromJspVariables('alert_change_delivery_before_delete'), severity: 'error', scope: 'DELETE_DIALOG' });
                return;
            }
            showLoader('Deleting device...');
            try {
                await actions.deleteDeviceAsync(appKey, userId, row.deviceName);
                const resp = await actions.loadAuthenticatorDevicesAsync(userId, appKey);
                setAuthenticatorDevices(resp.data || []);
            } catch (resp) {
                setAlert({ message: resp.message, severity: 'error' });
            } finally {
                hideLoader();
            }
        }
        setDeleteTarget(null);
    };

    // Render logic
    const tfaDetailsObject = userTfaDetails || {};
    let isFirstAndLastNameAvailable = false;
    if (tfaDetailsObject.firstName && JspUtil.isValueAvailable(tfaDetailsObject.firstName)
        && tfaDetailsObject.lastName && JspUtil.isValueAvailable(tfaDetailsObject.lastName)) {
        isFirstAndLastNameAvailable = true;
    }
    let isTfaEmailVerified = false;
    if (tfaDetailsObject.tfaEmailVerified && tfaDetailsObject.tfaEmailVerified === 'Y')
        isTfaEmailVerified = true;

    let tfaDeliveryPref = EMAIL_DELIVERY;
    const hasRegisteredPasskey = webAuthnDevices && webAuthnDevices.length > 0;
    const hasRegisteredAuthenticator = authenticatorDevices && authenticatorDevices.length > 0;
    if (tfaDetailsObject.isActiveTfaMobile && tfaDetailsObject.isActiveTfaMobile === 'Y') {
        tfaDeliveryPref = GOOGLE_AUTH_DELIVERY;
    }
    if (isAuthenticated) {
        if (tfaDetailsObject.isActiveTfaWebAuthn && tfaDetailsObject.isActiveTfaWebAuthn === 'Y') {
            tfaDeliveryPref = WEBAUTHN_DELIVERY;
        }
    }

    return (
        <PageContainer id="pageTfaRegister" dataAutomationId="page_tfa_register">
            {/* Setup Authenticator dialog (self-contained) */}
            <SetupAuthenticator
                open={showAuthenticator}
                onClose={closeAllDialogs}
                onSetAsDefault={() => onTfaDeliveryPreferenceOptionChange(GOOGLE_AUTH_DELIVERY, true)}
                onDeviceRegistered={(devices) => setAuthenticatorDevices(devices)}
                showHowToGoogleAuthenticatorDialog={() => setShowHowTo(true)}
            />

            {/* How To Google Authenticator dialog (opens on top of Setup Authenticator) */}
            <MuiDialog open={showHowTo} onClose={() => setShowHowTo(false)} maxWidth="md" fullWidth
                aria-labelledby="tfa-how-mobile-codes-title"
                aria-describedby="tfa-how-mobile-codes-description">
                <DialogTitleWithClose id="tfa-how-mobile-codes-title" onClose={() => setShowHowTo(false)} dataAutomationId="tfa_how_mobile_codes_title">{getLabelFromJspVariables('title_how_mobile_codes')}</DialogTitleWithClose>
                <DialogContent id="tfa-how-mobile-codes-description">
                    <Typography variant="body2" gutterBottom>
                        You can use any Authenticator App (e.g. Google/Microsoft Authenticator) to receive 2FA passcodes.
                        Once the app has been loaded to your mobile device, use the app to scan the QR code displayed on the
                        2FA registration page and then scan to complete registration. Once registration has been completed,
                        when prompted to enter the 2FA code, go to the app and enter the 6-digit code displayed.
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>{getLabelFromJspVariables('label_how_load_app')}</Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>{getLabelFromJspVariables('label_iphone_ipad')}</Typography>
                    <List dense>
                        <ListItem><ListItemText primary="1. Go to the App Store" /></ListItem>
                        <ListItem><ListItemText primary="2. Find Authenticator app" /></ListItem>
                        <ListItem><ListItemText primary="3. Download and install the application" /></ListItem>
                    </List>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>{getLabelFromJspVariables('label_android')}</Typography>
                    <List dense>
                        <ListItem><ListItemText primary="1. Go to Google Play" /></ListItem>
                        <ListItem><ListItemText primary="2. Find Authenticator app" /></ListItem>
                        <ListItem><ListItemText primary="3. Download and install the application" /></ListItem>
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setShowHowTo(false)}
                        data-automation-id="tfa_how_mobile_codes_ok_btn">{getLabelFromJspVariables('action_ok')}</Button>
                </DialogActions>
            </MuiDialog>

            {/* Setup Passkey dialog (self-contained) */}
            <SetupPasskeyDialog
                open={showPasskey}
                onClose={refreshTfaRegisterPage}
                onSetAsDefault={() => onTfaDeliveryPreferenceOptionChange(WEBAUTHN_DELIVERY, true)}
                onDeviceRegistered={onPasskeyDeviceRegistered}
            />

            {/* Registration Done dialog */}
            <MuiDialog open={showRegistrationDone} maxWidth="sm" fullWidth
                aria-labelledby="tfa-registration-done-title"
                aria-describedby="tfa-registration-done-description">
                <DialogTitleWithClose id="tfa-registration-done-title" severity="success" onClose={() => setShowRegistrationDone(false)} dataAutomationId="tfa_registration_done_title">
                    {getLabelFromJspVariables('title_tfa_registration')}
                </DialogTitleWithClose>
                <DialogContent id="tfa-registration-done-description">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {getLabelFromJspVariables('tfa_registration_complete')}
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid xs={4}><Typography variant="body2">{getLabelFromJspVariables('label_email')}</Typography></Grid>
                        <Grid xs={8}><Typography variant="body2">{userTfaDetails?.tfaEmail}</Typography></Grid>
                        <Grid xs={4}><Typography variant="body2">{getLabelFromJspVariables('label_delivery_option')}</Typography></Grid>
                        <Grid xs={8}>
                            <Typography variant="body2">
                                {userTfaDetails?.isActiveTfaWebAuthn === 'Y'
                                    ? 'Passkey (Yubikey/Fingerprint)'
                                    : userTfaDetails?.isActiveTfaMobile === 'Y'
                                        ? getLabelFromJspVariables('label_authenticator')
                                        : getLabelFromJspVariables('label_email_notification')}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        {getLabelFromJspVariables('tfa_reg_complete_note')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setShowRegistrationDone(false)}
                        data-automation-id="tfa_registration_done_btn">
                        {getLabelFromJspVariables('action_done')}
                    </Button>
                </DialogActions>
            </MuiDialog>

            {/* Delete confirmation dialog */}
            <MuiDialog open={!!deleteTarget} onClose={() => { setDeleteTarget(null); if (alert?.scope === 'DELETE_DIALOG') setAlert(null); }} maxWidth="sm" fullWidth
                aria-labelledby="tfa-delete-target-title"
                aria-describedby="tfa-delete-target-description">
                <DialogTitleWithClose id="tfa-delete-target-title" severity="warning" onClose={() => { setDeleteTarget(null); if (alert?.scope === 'DELETE_DIALOG') setAlert(null); }} dataAutomationId="tfa_delete_target_title">{getLabelFromJspVariables('title_confirm_delete')}</DialogTitleWithClose>
                <DialogContent id="tfa-delete-target-description">
                    {alert?.scope === 'DELETE_DIALOG' &&
                        <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                    <p>{getLabelFromJspVariables('question_delete_confirm',
                        deleteTarget?.isPassKey ? getLabelFromJspVariables('label_type_passkey') : getLabelFromJspVariables('label_type_device'),
                        deleteTarget?.row?.deviceName)}</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeleteTarget(null)}
                        data-automation-id="tfa_delete_target_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                    <Button variant="contained" onClick={confirmDelete}
                        data-automation-id="tfa_delete_target_confirm_btn">{getLabelFromJspVariables('action_confirm')}</Button>
                </DialogActions>
            </MuiDialog>

            <Box style={panelStyle}>
                <UserMenu
                    authenticatedUser={authenticatedUser}
                    onLogout={logoutUserToken}
                    onNavigate={handleNavigate}
                />
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="tfa_register_header">{getLabelFromJspVariables('title_tfa_registration')}</PageHeader>
                <Box>
                    {alert && !alert.scope && <Alert id="pageTfaRegisterAlert" data-automation-id="page_tfa_register_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                </Box>

                {userTfaDetails ?
                    <>
                {isFirstAndLastNameAvailable && <>
                    <TextInput label={getLabelFromJspVariables('label_first_name')} id="first_name_input"
                        dataAutomationId="first_name_input"
                        name="firstName" required
                        value={tfaDetailsObject.firstName} onChange={onFirstNameChange} />
                    <TextInput label={getLabelFromJspVariables('label_last_name')} id="last_name_input"
                        dataAutomationId="last_name_input"
                        name="lastName" required
                        value={tfaDetailsObject.lastName} onChange={onLastNameChange} />
                </>}
                <TextInput label={getLabelFromJspVariables('label_email_required')} id="email_input"
                    dataAutomationId="email_input"
                    name="tfaEmail" required type="email"
                    value={tfaDetailsObject.tfaEmail || ''}
                    disabled={tfaDetailsObject.app === 'GLOBAL'}
                    onChange={onEmailChange} />
                {tfaDetailsObject && tfaDetailsObject.RESTRICTED_EMAIL_DOMAINS && JspUtil.isValueAvailable(tfaDetailsObject.RESTRICTED_EMAIL_DOMAINS) &&
                    <Box>
                        {JspUtil.getLabelFromJspVariables('restricted_domain_list') + tfaDetailsObject.RESTRICTED_EMAIL_DOMAINS}
                    </Box>
                }
                {isTfaEmailVerified ?
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2, gap: 1 }}>
                            <img alt="verified" src={constants.IMAGES_VERIFIED_PNG} style={{ marginBottom: '8px' }} />
                                {getLabelFromJspVariables('status_email_verified')}
                        </Box>
                        <Box>
                            <Box>{getLabelFromJspVariables('choose_delivery_option')}</Box>
                            <RadioGroup
                                label={getLabelFromJspVariables('select_preferred_delivery')}
                                labelId="lblPasscodeDeliveryOption"
                                labelDataAutomationId="lbl_passcode_delivery_option"
                                name="tfaDeliveryPreference"
                                dataAutomationId="tfa_delivery_preference"
                                required
                                value={tfaDeliveryPref}
                                onChange={(e) => onTfaDeliveryPreferenceOptionChange(e.target.value)}
                                helperText={getLabelFromJspVariables('note_3min_life')}
                                options={[
                                    {
                                        value: EMAIL_DELIVERY,
                                        label: getLabelFromJspVariables('label_email_notification'),
                                        id: 'enableEmail',
                                        dataAutomationId: 'enable_email',
                                    },
                                    ...(isAuthenticated ? [{
                                        value: WEBAUTHN_DELIVERY,
                                        label: 'Passkey (Yubikey/Fingerprint)',
                                        disabled: !hasRegisteredPasskey,
                                        hint: !hasRegisteredPasskey ? '(Must configure Passkey to allow this option - See below)' : '',
                                        id: 'enableWebAuthn',
                                        dataAutomationId: 'enable_web_authn',
                                    }] : []),
                                    {
                                        value: GOOGLE_AUTH_DELIVERY,
                                        label: getLabelFromJspVariables('label_authenticator'),
                                        disabled: !hasRegisteredAuthenticator,
                                        hint: !hasRegisteredAuthenticator ? '(Must configure Authenticator app to allow this option - See below)' : '',
                                        id: 'enableGoogleAuth',
                                        dataAutomationId: 'enable_google_auth',
                                    },
                                ]}
                            />
                        </Box>
                        {(tfaDeliveryPref === GOOGLE_AUTH_DELIVERY || tfaDeliveryPref === EMAIL_DELIVERY || tfaDeliveryPref === WEBAUTHN_DELIVERY) &&
                            <Button variant="contained" fullWidth
                                onClick={() => verifyNSaveTfaDetails()}
                                data-automation-id="tfa_register_done_btn">
                                {getLabelFromJspVariables('action_save')}
                            </Button>
                        }
                        <hr style={{ marginTop: '0.65em', marginBottom: '1.0rem', borderTop: '2px solid #d2d4d3' }} />
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle1" component="h3" fontWeight={600}>
                                        {getLabelFromJspVariables('label_passkey')}
                                    </Typography>
                                    <Link component="button" type="button"
                                        sx={{ color: '#87CEEB', fontSize: 'small' }}
                                        aria-label="How to setup Passkey - opens help dialog"
                                        onClick={(e) => { e.preventDefault(); setPasskeyHelpOpen(true); }}
                                        data-automation-id="tfa_register_passkey_help_link">
                                        {getLabelFromJspVariables('action_how_setup_passkey')}
                                    </Link>
                                    <PasskeyHelpDialog open={passkeyHelpOpen} onClose={() => setPasskeyHelpOpen(false)} />
                                </Box>
                                <Tooltip title={getLabelFromJspVariables('label_add_passkey')}>
                                    <IconButton id="btnSetupPasskey" data-automation-id="btn_setup_passkey" onClick={showSetupPasskeyDialog}                                        aria-label="Add Passkey">
                                        <AddIcon sx={{ fontSize: '1.5rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead sx={{ '& th': { fontWeight: 'bold' } }}>
                                        <TableRow sx={{ backgroundColor: '#ececec' }}>
                                            <TableCell>{getLabelFromJspVariables('label_passkey_name_col')}</TableCell>
                                            <TableCell align="center">{getLabelFromJspVariables('label_passkey_reg_date')}</TableCell>
                                            <TableCell align="center">{getLabelFromJspVariables('label_delete_passkey')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    {webAuthnDevices ?
                                        <TableBody sx={stripedTableBodySx}>
                                            {webAuthnDevices.length === 0 ?
                                                <EmptyDevicesRow message={getLabelFromJspVariables('label_no_passkeys')} />
                                                :
                                                webAuthnDevices.map((row, i) => {
                                                    if (row) {
                                                        return (
                                                            <TableRow key={i}>
                                                                <TableCell>{row.deviceName}</TableCell>
                                                                <TableCell align="center">{(new Date(row.createdDate)).toLocaleDateString()}</TableCell>
                                                                <TableCell align="center">
                                                                    <Tooltip title={`Delete passkey ${row.deviceName}`}>
                                                                        <IconButton                                                                            aria-label={`Delete passkey ${row.deviceName}`}
                                                                            onClick={() => { setAlert(null); setDeleteTarget({ row, isPassKey: true }); }}>
                                                                            <DeleteOutlineIcon sx={{ fontSize: '1.25rem' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }
                                                    return null;
                                                })
                                            }
                                        </TableBody>
                                        :
                                        <DeviceTableSkeleton />
                                    }
                                </Table>
                            </TableContainer>
                        </Box>
                        <hr style={{ marginTop: '0.65em', marginBottom: '1.0rem', borderTop: '2px solid #d2d4d3' }} />
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle1" component="h3" fontWeight={600}>
                                        {getLabelFromJspVariables('label_authenticator_app_full')}
                                    </Typography>
                                    <Link component="button" type="button"
                                        sx={{ color: '#87CEEB', fontSize: 'small' }}
                                        aria-label="How to setup Authenticator - opens help dialog"
                                        onClick={(e) => { e.preventDefault(); setShowHowTo(true); }}
                                        data-automation-id="tfa_register_authenticator_help_link">
                                        {getLabelFromJspVariables('action_how_setup_authenticator')}
                                    </Link>
                                </Box>
                                <Tooltip title={getLabelFromJspVariables('label_add_authenticator')}>
                                    <IconButton id="btnSetupNewDevice" data-automation-id="btn_setup_new_device" onClick={showSetupAuthenticatorDialog}                                        aria-label="Add Authenticator">
                                        <AddIcon sx={{ fontSize: '1.5rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead sx={{ '& th': { fontWeight: 'bold' } }}>
                                        <TableRow sx={{ backgroundColor: '#ececec' }}>
                                            <TableCell>{getLabelFromJspVariables('label_device_name')}</TableCell>
                                            <TableCell align="center">{getLabelFromJspVariables('label_device_reg_date')}</TableCell>
                                            <TableCell align="center">{getLabelFromJspVariables('label_delete_device')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    {authenticatorDevices ?
                                        <TableBody sx={stripedTableBodySx}>
                                            {authenticatorDevices.length === 0 ?
                                                <EmptyDevicesRow message={getLabelFromJspVariables('label_no_devices')} />
                                                :
                                                authenticatorDevices.map((row, i) => {
                                                    if (row) {
                                                        return (
                                                            <TableRow key={i}>
                                                                <TableCell>{row.deviceName}</TableCell>
                                                                <TableCell align="center">{(new Date(row.createdDate)).toLocaleDateString()}</TableCell>
                                                                <TableCell align="center">
                                                                    <Tooltip title={`Delete device ${row.deviceName}`}>
                                                                        <IconButton                                                                            aria-label={`Delete device ${row.deviceName}`}
                                                                            onClick={() => { setAlert(null); setDeleteTarget({ row, isPassKey: false }); }}>
                                                                            <DeleteOutlineIcon sx={{ fontSize: '1.25rem' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }
                                                    return null;
                                                })
                                            }
                                        </TableBody>
                                        :
                                        <DeviceTableSkeleton />
                                    }
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                    :
                    <Button variant="contained" fullWidth
                        onClick={async () => {
                            setAlert(null);
                            try {
                                await actions.sendEmailVerificationCodeAsync(userTfaDetails.tfaEmail);
                                setAlert({ message: getLabelFromJspVariables('alert_verification_email_sent'), severity: 'success' });
                            } catch (resp) {
                                setAlert({ message: resp.message || getLabelFromJspVariables('err_verification_email_failed'), severity: 'error' });
                            }
                        }}
                        data-automation-id="tfa_register_send_confirmation_btn">
                        {getLabelFromJspVariables('send_confirmation_email')}
                    </Button>
                }
                    </>
                    :
                    <TfaFormSkeleton />
                }
            </Box>
        </PageContainer>
    );
};

export default PageTfaRegister;
